import { BalanceData, GearItem, GearOwner, GearTarget, PlayerProfile, affinityOf, itemPower, primaryValue, sameOwner, seatScore } from "@highmarch/domain";

/**
 * "Equip best" (DESIGN §9.4, §7): which of the player's items go on which owner. Plain integer bookkeeping on the gear
 * score — no sim, no probe — so the hub can ask it every render and the Gear sheet can apply it in one tap. Where an
 * item goes matters as much as what it is (gear is per tower TYPE and a level wants particular types), so the owners
 * come ranked per slot: for a level, the seats its precomputed target lists (where the tuning's sensible player put
 * that slot's items); without a level, every owned type, affinity first.
 */

/** One item's share of the gear score (§7): exactly the term `gearScore` adds for it while it is equipped. */
export function itemScore(balance: BalanceData, item: GearItem): number {
  return itemPower(balance, item);
}

/** Somewhere gear can go, with the persistent level that gates it (an item needs its item level ≤ level). */
export interface Seat { readonly owner: GearOwner; readonly level: number }

/** Per gear slot index: the owners that slot's items may go on, best first. */
export type SeatPlan = readonly (readonly Seat[])[];

const BASE: GearOwner = { target: GearTarget.Base, towerIndex: 0 };
const towerOwner = (towerIndex: number): GearOwner => ({ target: GearTarget.Tower, towerIndex });
const seatOf = (profile: PlayerProfile, owner: GearOwner): Seat => ({ owner, level: owner.target === GearTarget.Base ? profile.baseLevel : profile.towerLevels[owner.towerIndex] });

/** Every tower type: the roster when the caller does not say which types it owns. */
export const allTowers = (balance: BalanceData): boolean[] => balance.towers.map((t) => !t.isBase);

/** Seats over a roster (true = owned; the base entry is ignored): per tower slot, types whose affinity is that slot's stat first, then the highest level. */
export function rosterSeats(profile: PlayerProfile, roster: readonly boolean[]): SeatPlan {
  const B = profile.balance;
  const types = B.towers.map((t, i) => i).filter((i) => !B.towers[i].isBase && roster[i]);
  return B.gear.slots.map((slot) => {
    if (slot.target === GearTarget.Base) return [seatOf(profile, BASE)];
    const affine = (i: number) => Number(affinityOf(B, towerOwner(i)) === slot.primary);
    return [...types].sort((a, b) => affine(b) - affine(a) || profile.towerLevels[b] - profile.towerLevels[a] || a - b).map((i) => seatOf(profile, towerOwner(i)));
  });
}

/** Seats for one level: the base, and per tower slot the owned types the level's target lists, in its order. */
export function levelSeats(profile: PlayerProfile, level: number, roster: readonly boolean[]): SeatPlan {
  const B = profile.balance;
  const target = B.readiness.targets[level - 1];
  if (target === undefined) return rosterSeats(profile, roster);
  return B.gear.slots.map((slot) => (slot.target === GearTarget.Base ? [seatOf(profile, BASE)] : target.seats[slot.index].filter((i) => roster[i]).map((i) => seatOf(profile, towerOwner(i)))));
}

const seated = (seats: SeatPlan, it: GearItem): boolean => it.equippedTo !== null && seats[it.slot].some((s) => sameOwner(s.owner, it.equippedTo!));

/**
 * Gear score of the items equipped on these seats only (gear on a type the level does not field does nothing there),
 * each on the owner wearing it (`seatScore`: affinity and Mythic effects count, as the readiness targets do).
 */
export function seatedGearScore(balance: BalanceData, items: readonly GearItem[], seats: SeatPlan): number {
  let sum = 0;
  for (const it of items) if (seated(seats, it)) sum += seatScore(balance, it, it.equippedTo!);
  return sum;
}

export interface LoadoutMove { readonly item: GearItem; readonly from: GearOwner | null; readonly to: GearOwner | null }

export interface LoadoutPlan {
  /** Every item whose place changes: equipped from the bag or a bench tower, moved to a better seat, or taken off for a better one. */
  readonly moves: readonly LoadoutMove[];
  /** The items the plan puts onto the seats from off them — the bag, or a type the scope does not field — each
   *  filling an empty seat or beating what a seat holds. Its length is the EQUIP n count (`equipCount`). */
  readonly equips: readonly GearItem[];
  /** Gear score on the seats before and after. */
  readonly scoreBefore: number;
  readonly scoreAfter: number;
}

/** Where an item starts, as the planner's tie-break ranks it (higher is kept first among equal scores). */
const OFF_SEAT = 0, FROM_BAG = 1, ON_SEAT = 2;

/**
 * The weight of putting an item on a seat, as whole numbers in lexicographic tiers (each tier's largest possible sum
 * over a slot's seats stays below one step of the tier above): first the item's score on that seat (`seatScore`:
 * affinity and Mythic effects count), then an item staying on the seat it already wears, then where it comes from
 * (a seat, the bag, a bench tower), then the stronger item on the better-ranked seat (the level's seat order).
 */
const TIER_SCORE = 1e8, TIER_STAY = 1e6, TIER_WHERE = 1e4;
/** A pairing that may not happen (the item's level requirement is above the seat's level). */
const FORBIDDEN = -1e15;

/**
 * The best loadout over `seats`: per slot, the assignment of items to seats with the highest total seat score (an
 * exact max-weight matching, Hungarian method — a slot has at most a handful of seats), ties broken as `weight` says.
 * Items worn by an owner outside `seats` (a tower type the level does not field) are in the pool too, after the bag
 * among equals: gear a player equipped for an earlier level would otherwise sit stranded on its bench tower while
 * this level's seats go short (the readiness calibration equips from a fresh bag, so it pools everything). An
 * off-seat item the plan does not pick stays where it is.
 */
export function planLoadout(balance: BalanceData, items: readonly GearItem[], seats: SeatPlan): LoadoutPlan {
  const moves: LoadoutMove[] = [];
  const equips: GearItem[] = [];
  let scoreAfter = 0;
  for (const slot of balance.gear.slots) {
    const ranked = seats[slot.index] ?? [];
    const whereOf = (it: GearItem) => (it.equippedTo === null ? FROM_BAG : seated(seats, it) ? ON_SEAT : OFF_SEAT);
    // Each item's score, place and primary are worked out once: the sort and the matching below asked for them
    // thousands of times on a big bag (readiness runs on every hub frame that shows it; readiness.test "is cheap").
    const facts = new Map<GearItem, { score: number; where: number; primary: ReturnType<typeof primaryValue> }>();
    for (const it of items) if (it.slot === slot.index) facts.set(it, { score: itemScore(balance, it), where: whereOf(it), primary: primaryValue(balance, it) });
    const fact = (it: GearItem) => facts.get(it)!;
    const where = (it: GearItem) => fact(it).where;
    // A stable order (strongest first, then the old tie-breaks) so equal matchings resolve the same way every time.
    const pool = items.filter((it) => it.slot === slot.index).sort((a, b) => fact(b).score - fact(a).score || where(b) - where(a)
      || fact(b).primary.cmp(fact(a).primary) || a.id - b.id);
    const rows = ranked.length, cols = pool.length;
    const weights = new Float64Array(rows * cols);
    for (let s = 0; s < rows; s++) {
      const seat = ranked[s];
      for (let i = 0; i < cols; i++) {
        const it = pool[i];
        const worn = it.equippedTo !== null && sameOwner(it.equippedTo, seat.owner);
        // An item already worn here stays wearable here (a folded v1 +N may sit above its wearer's level).
        weights[s * cols + i] = seat.level < it.level && !worn ? FORBIDDEN
          : seatScore(balance, it, seat.owner) * TIER_SCORE + (worn ? 1 : 0) * TIER_STAY + where(it) * TIER_WHERE + (rows - s) * fact(it).score;
      }
    }
    const pick = assign(rows, cols, (s, i) => weights[s * cols + i]);
    const placed = new Map<number, GearOwner>();
    pick.forEach((i, s) => {
      if (i < 0) return;
      placed.set(pool[i].id, ranked[s].owner);
      scoreAfter += seatScore(balance, pool[i], ranked[s].owner);
    });
    for (const it of pool) {
      const to = placed.get(it.id) ?? null;
      if (to === null && where(it) === OFF_SEAT) continue;
      const same = to === null ? it.equippedTo === null : it.equippedTo !== null && sameOwner(it.equippedTo, to);
      if (same) continue;
      moves.push({ item: it, from: it.equippedTo, to });
      if (to !== null && where(it) !== ON_SEAT) equips.push(it);
    }
  }
  return { moves, equips, scoreBefore: seatedGearScore(balance, items, seats), scoreAfter };
}

/**
 * Max-weight assignment of `rows` seats to `cols` items (Hungarian method, O(rows² · cols)): per seat the chosen item
 * index, or −1 for a seat left empty (every weight ≤ 0 there, or forbidden). Each seat may also take "nothing" at
 * weight 0, so a seat never takes an item it cannot wear.
 */
export function assign(rows: number, cols: number, weight: (row: number, col: number) => number): number[] {
  const m = cols + rows;                                   // real items, then one "empty" column per seat
  const w = (r: number, c: number) => (c < cols ? weight(r, c) : 0);
  const INF = Number.MAX_VALUE;
  const u = new Array<number>(rows + 1).fill(0), v = new Array<number>(m + 1).fill(0);
  const p = new Array<number>(m + 1).fill(0), way = new Array<number>(m + 1).fill(0);
  for (let r = 1; r <= rows; r++) {
    p[0] = r;
    let j0 = 0;
    const minv = new Array<number>(m + 1).fill(INF), used = new Array<boolean>(m + 1).fill(false);
    do {
      used[j0] = true;
      const r0 = p[j0];
      let delta = INF, j1 = 0;
      for (let j = 1; j <= m; j++) {
        if (used[j]) continue;
        const cur = -w(r0 - 1, j - 1) - u[r0] - v[j];
        if (cur < minv[j]) { minv[j] = cur; way[j] = j0; }
        if (minv[j] < delta) { delta = minv[j]; j1 = j; }
      }
      for (let j = 0; j <= m; j++) {
        if (used[j]) { u[p[j]] += delta; v[j] -= delta; } else minv[j] -= delta;
      }
      j0 = j1;
    } while (p[j0] !== 0);
    do { const j1 = way[j0]; p[j0] = p[j1]; j0 = j1; } while (j0 !== 0);
  }
  const out = new Array<number>(rows).fill(-1);
  for (let j = 1; j <= m; j++) if (p[j] > 0 && j - 1 < cols && w(p[j] - 1, j - 1) > FORBIDDEN) out[p[j] - 1] = j - 1;
  return out;
}

/** Which seats "Equip best" and the fits-now count use: a level's, or (no level) every type in the roster. */
export interface LoadoutScope {
  /** Plan for this level's seats (its readiness target). Omit for the whole roster. */
  readonly level?: number;
  /** Tower types owned (true); defaults to every type. `LevelCatalog.ownedTowers()` gives the real one. */
  readonly roster?: readonly boolean[];
}

export function seatsFor(profile: PlayerProfile, scope: LoadoutScope = {}): SeatPlan {
  const roster = scope.roster ?? allTowers(profile.balance);
  return scope.level !== undefined ? levelSeats(profile, scope.level, roster) : rosterSeats(profile, roster);
}

/** The profile's best loadout for a scope. */
export function bestLoadout(profile: PlayerProfile, scope: LoadoutScope = {}): LoadoutPlan {
  return planLoadout(profile.balance, profile.gear, seatsFor(profile, scope));
}

/**
 * The EQUIP n number (hub badge, battle card's EQUIP n, readiness `equippable`, the Gear sheet's Equip best): how many
 * items "Equip best" puts onto the scope's seats. Every surface asks this with the same scope so they agree.
 */
export function equipCount(profile: PlayerProfile, scope: LoadoutScope = {}): number {
  return bestLoadout(profile, scope).equips.length;
}

/** Items that fit an empty seat or beat the one equipped there (by `itemScore`): exactly what "Equip best" puts on. */
export function equippableItems(profile: PlayerProfile, scope: LoadoutScope = {}): GearItem[] {
  return [...bestLoadout(profile, scope).equips];
}

/**
 * Applies a plan through the profile's own equip rules (take off everything that moves, then put each on). Returns
 * true, or the first refusal — a plan made from this profile's current state is never refused.
 */
export function applyLoadout(profile: PlayerProfile, plan: LoadoutPlan): true | string {
  for (const m of plan.moves) if (m.from !== null) { const r = profile.unequipGear(m.item.id); if (r !== true) return r; }
  for (const m of plan.moves) if (m.to !== null) { const r = profile.equipGear(m.item.id, m.to); if (r !== true) return r; }
  return true;
}
